import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Box, Flex, Text, useTheme} from '@chakra-ui/react';
import useGetUserLocation from '../../hooks/useUserLocation';
import {calculateDistance, convertFootToMiles} from '../../utils/distance';
import {
  initializeDynamicPositioning,
  positionCardAboveClosestVenue,
  resetCardPosition,
  Position,
} from './positioningHelper';
import './styles.css';

interface Place {
  name: string;
  latitude: number;
  longitude: number;
}

export interface YouAreHereCardProps {
  // Optional props to allow controlling card placement
  closestPlaceName?: string;
  mapPlaces?: Array<Place>;
  placeOrder?: Array<string>;
  shouldPositionDynamically?: boolean; // Whether to position the card dynamically
}

/**
 * A card that displays "You Are Here!" with the user's current location marker
 *
 * Features:
 * - Shows user's current location with an animated marker
 * - Displays distance to nearby venues
 * - Can dynamically position itself above the venue closest to user's location
 * - Mobile-optimized with touch-friendly targets (min 44px)
 * - Smooth animations for positioning changes
 * - Graceful fallbacks for loading and error states
 *
 * Usage:
 * <YouAreHereCard
 *   shouldPositionDynamically={true}
 *   mapPlaces={venues}
 * />
 */
const YouAreHereCard: React.FC<YouAreHereCardProps> = ({
  closestPlaceName,
  mapPlaces,
  shouldPositionDynamically = false,
}) => {
  const theme = useTheme();
  const userLocation = useGetUserLocation();
  const [closestPlace, setClosestPlace] = useState<string | null>(closestPlaceName || null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [positioned, setPositioned] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Memoize the map places to prevent unnecessary recalculations
  const memoizedMapPlaces = useMemo(
    () => mapPlaces,
    [mapPlaces ? JSON.stringify(mapPlaces) : null]
  );
  // We don't need to initialize anything in this version of the code
  // The positioning will be handled in a separate effect when we have data
  // Find the closest place if not provided and we have mapPlaces data
  useEffect(() => {
    setIsLoading(true);

    if (closestPlaceName) {
      setClosestPlace(closestPlaceName);
      setIsLoading(false);
      return;
    }

    if (
      memoizedMapPlaces &&
      memoizedMapPlaces.length > 0 &&
      userLocation?.data?.latitude &&
      userLocation?.data?.longitude
    ) {
      const userCoords = {
        latitude: userLocation.data.latitude,
        longitude: userLocation.data.longitude,
      };

      // Initialize with first place
      let closestPlaceObj = memoizedMapPlaces[0];
      let shortestDistance = calculateDistance(userCoords, {
        latitude: memoizedMapPlaces[0].latitude,
        longitude: memoizedMapPlaces[0].longitude,
      });

      // Convert to miles to check threshold
      let shortestDistanceInMiles = convertFootToMiles(shortestDistance);

      // Find the closest place
      memoizedMapPlaces.forEach((place) => {
        const distance = calculateDistance(userCoords, {
          latitude: place.latitude,
          longitude: place.longitude,
        });

        if (distance < shortestDistance) {
          shortestDistance = distance;
          shortestDistanceInMiles = convertFootToMiles(distance);
          closestPlaceObj = place;
        }
      });

      // Only set the closest place if it's within 0.5 miles
      if (shortestDistanceInMiles < 0.5) {
        setClosestPlace(closestPlaceObj.name);
      } else {
        // If no places are within 0.5 miles, set closestPlace to null
        setClosestPlace(null);
      }

      setIsLoading(false);
    } else if (userLocation?.data) {
      // We have user location but no map places data
      setIsLoading(false);
    }
  }, [memoizedMapPlaces, userLocation?.data, closestPlaceName]);

  // Convert SVG for user location icon to a data URL
  const userLocationSvg = `
    <svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="20" fill="#276ACD" opacity="0.2">
        <animate attributeName="r" values="15;20;15" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.2;0.3;0.2" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="22" cy="22" r="12" fill="#276ACD" opacity="0.4" />
      <circle cx="22" cy="22" r="8" fill="white" />
      <circle cx="22" cy="22" r="4" fill="#276ACD" />
    </svg>
  `;

  const userIconUrl = `data:image/svg+xml;base64,${btoa(userLocationSvg.trim())}`; // Calculate distance from the user's location to the closest place
  const distanceToClosest =
    userLocation?.data && closestPlace && memoizedMapPlaces
      ? (() => {
          const closestPlaceObj = memoizedMapPlaces.find((p) => p.name === closestPlace);
          if (closestPlaceObj) {
            const distanceInFeet = calculateDistance(
              {latitude: userLocation.data.latitude, longitude: userLocation.data.longitude},
              {latitude: closestPlaceObj.latitude, longitude: closestPlaceObj.longitude}
            );
            const distanceInMiles = convertFootToMiles(distanceInFeet);

            if (distanceInMiles > 0.1) {
              return distanceInMiles.toFixed(1) + ' mi';
            }
            return distanceInFeet.toFixed(0) + ' feet'; // Return distance in feet if less than 0.15 miles

            // Format distance for display using community-focused language
            // Note: We know distanceInMiles is < 0.5 because otherwise we wouldn't render the card
            // if (distanceInMiles < 0.1) {
            //   return 'Just steps away';
            // } else if (distanceInMiles < 0.25) {
            //   return 'Nearby';
            // } else {
            //   // Between 0.25 and 0.5 miles
            //   return 'Short stroll';
            // }
          }
          return 'In your area';
        })()
      : 'Now';
  // Format the display text
  const distanceText = closestPlace ? `${distanceToClosest} from ${closestPlace}` : 'Now'; // Don't render anything in these cases:
  // Handle dynamic positioning
  useEffect(() => {
    if (!shouldPositionDynamically || isLoading || !closestPlace) {
      // If dynamic positioning is disabled or no place is close enough, reset the card position
      if (cardRef.current && positioned) {
        resetCardPosition(cardRef.current);
        setPositioned(false);
      }
      return;
    }

    // Create a position object from user location data
    if (userLocation?.data?.latitude && userLocation?.data?.longitude) {
      // Find the closest venue and position the card
      if (memoizedMapPlaces && cardRef.current) {
        const closestPlaceObj = memoizedMapPlaces.find((p) => p.name === closestPlace);

        if (closestPlaceObj) {
          // Calculate distance to ensure it's within our threshold
          const distanceInFeet = calculateDistance(
            {latitude: userLocation.data.latitude, longitude: userLocation.data.longitude},
            {latitude: closestPlaceObj.latitude, longitude: closestPlaceObj.longitude}
          );
          const distanceInMiles = convertFootToMiles(distanceInFeet);

          // Only attempt positioning if within 0.5 miles
          if (distanceInMiles < 0.5) {
            // console.log(
            //   `Attempting to position card above ${closestPlace} (${distanceInMiles.toFixed(
            //     2
            //   )} miles away)...`
            // );

            // Ensure DOM is loaded before attempting to position the card
            // This helps with timing issues in case the venues aren't rendered yet
            setTimeout(() => {
              const success = positionCardAboveClosestVenue(cardRef.current, closestPlaceObj);
              setPositioned(success);

              if (!success) {
                console.warn(`Failed to position card. Will retry in 1 second...`);
                // Retry once more after a delay in case the DOM is still updating
                setTimeout(() => {
                  const retrySuccess = positionCardAboveClosestVenue(
                    cardRef.current,
                    closestPlaceObj
                  );
                  setPositioned(retrySuccess);
                }, 1000);
              }
            }, 100);
          } else {
            // Reset position if somehow we have a place but it's too far
            resetCardPosition(cardRef.current);
            setPositioned(false);
          }
        } else {
          console.warn(`Could not find matching place object for ${closestPlace}`);
        }
      }
    }

    // Clean up function to reset position when component unmounts or changes
    return () => {
      // Store a reference to cardRef.current before it potentially becomes null during unmounting
      const card = cardRef.current;
      if (!card) return;

      try {
        // First, remove the positioned class to make it invisible via CSS transitions
        card.classList.remove('positioned');

        // Set a timeout to let CSS transitions complete before resetting position
        const timeoutId = setTimeout(() => {
          // Check if the element is still in the document before manipulation
          if (document.body.contains(card)) {
            resetCardPosition(card);
          }
        }, 150);

        // This cleanup function is called when the component unmounts or dependencies change
        clearTimeout(timeoutId);
      } catch (error) {
        console.error('Error in cleanup function:', error);
      }
    };
  }, [shouldPositionDynamically, closestPlace, isLoading, userLocation?.data, memoizedMapPlaces]);

  // 1. If we don't have location data
  // 2. If there's no place within 0.5 miles (closestPlace will be null)
  if (!userLocation?.data?.latitude || !userLocation?.data?.longitude) {
    // If we're actively fetching the location, show a loading state
    if (userLocation?.isLoading) {
      return null;
    }
    return null;
  }

  // This style matches the <summary> elements in the Events By Place section
  return (
    <Box
      ref={cardRef}
      id={`youarehere-${closestPlace ? closestPlace.toLowerCase().replace(/\s+/g, '-') : 'card'}`}
      padding={positioned ? '15px' : 0}
      //   backgroundImage="linear-gradient(to bottom, #f8fbff, #e9f3ff)"
      background="white"
      border={`1px solid ${theme.colors.rose}`}
      borderRadius="8px"
      fontWeight="bold"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      marginBottom={positioned ? '24px !important' : 0}
      boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
      position="relative"
      fontSize="17px"
      color="#0066cc"
      transition="all 0.3s ease"
      //   _hover={{
      //     transform: 'translateY(-2px)',
      //     boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      //   }}
      height={positioned ? 'auto' : '0px'} // Allow height to expand when positioned
      minHeight={positioned ? '44px' : 0} // Mobile-friendly touch target height
      width={positioned ? '100%' : 0}
      data-closest-place={closestPlace}
      data-is-loading={isLoading.toString()}
      data-positioned={positioned.toString()}
      data-dynamic-positioning={shouldPositionDynamically.toString()}
      role="status" // Accessibility role
      aria-live="polite" // Announces changes to screen readers
      className={`youarehere-card ${positioned ? 'positioned' : ''}`}>
      <Flex align="center">
        <Box
          width="44px"
          height="44px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mr={4}>
          <Box as="img" src={userIconUrl} alt="Your location" width="44px" height="44px" />
        </Box>

        <Text color={theme.colors.rose} fontWeight="bold" fontSize="17px">
          You Are {distanceText}
        </Text>
      </Flex>
    </Box>
  );
};

export default YouAreHereCard;
